import { useBrowserStorage } from '@aspendental/shared-utils/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { FACILITY_CODE_STORAGE_KEY, IUser } from '@shared-components-web/constants';
import { BffBrandType, FacilityBrand } from '@shared-components-web/gqlConstants';
import { getBffFacilityById } from '@shared-components-web/services/getBffFacilityById';
import { getBffNearByFacilitiesByLocation } from '@shared-components-web/services/getBffNearByFacilitiesByLocation';
import { Maybe, TAGBrand } from '@shared-components-web/types';
import { cacheFn } from '@shared-components-web/utils/cacheFn/cacheFn';
import { getGraphHeaders } from '@shared-components-web/utils/getGraphHeaders';
import { mapFacilities } from '@shared-components-web/utils/mapFacilitiesResults/mapFacilitiesResults';

type NearbyFacility = any;
type BffFacility = any;
type BffFacilityFieldsFragment = any;
type FacilityFieldsFragment = any;

const FACILITY_FIELDS_FRAGMENT = `
fragment FacilityFields on Facility {
	name
	code
	brand
	scheduling {
		schedulingUrl
	}
	technicalInfrastructure {
		phoneNumber
		faxNumber
		email
	}
	address {
		address1
		address2
		city
		stateCode
		zipCode
	}
	location {
		latitude
		longitude
		dst
		landmarks
		neighbouringAreas
	}
	workingHours {
		date
		open {
			from
			to
		}
	}
}
`;

const FACILITY_BY_CODE_QUERY = `
query facilityByCode($code: String!, $brand: FacilityBrand!) {
	facility(code: $code, brand: $brand) {
		...FacilityFields
	}
}
${FACILITY_FIELDS_FRAGMENT}
`;

const NEARBY_FACILITIES_QUERY = `
query nearbyFacilities(
	$filter: NearbyFacilitiesLocationFilter!
	$skip: NonNegativeInt = 0
	$take: NonNegativeInt = 5
) {
	nearbyFacilities(filter: $filter, skip: $skip, take: $take) {
		items {
			distanceInMiles
			facility {
				...FacilityFields
			}
		}
		hasMore
	}
	callCenterHours {
		day
		intervals {
			startTime
			endTime
		}
	}
}
${FACILITY_FIELDS_FRAGMENT}
`;

export type OfficeInfoType =
	| (FacilityFieldsFragment & { attributes?: BffFacilityFieldsFragment['attributes']; email?: string | null })
	| null;

interface INearbyFacilities {
	appName: string;
	appVersion: string;
	graphqlUrl: string;
	brand: TAGBrand;
	coords: {
		latitude: number;
		longitude: number;
	};
	withinRadius?: number;
}

async function getFacilityDetails(
	brand: TAGBrand,
	facilityCode: string,
	appName: string,
	appVersion: string,
	graphqlUrl: string
) {
	const facilityBrand = brandThemeToFacilityBrand(brand);
	const { getBeffeGraphQlClient } = await import('@src/lib/client');
	const facilityInfoResp = await getBeffeGraphQlClient(graphqlUrl)
		.query<{ facility: FacilityFieldsFragment }>(
			FACILITY_BY_CODE_QUERY,
			{
				code: facilityCode,
				brand: facilityBrand as never,
			},
			{ fetchOptions: { headers: getGraphHeaders(appName, appVersion) } }
		)
		.toPromise();
	return facilityInfoResp.data?.facility as FacilityFieldsFragment;
}

export const getNearbyFacilities = cacheFn(async function getNearbyFacilities({
	appName,
	appVersion,
	graphqlUrl,
	brand,
	coords,
	withinRadius = 200,
}: INearbyFacilities): Promise<NearbyFacility[]> {
	const { latitude, longitude } = coords;
	if (!latitude || !longitude) {
		return Promise.reject(`Invalid coordinates of latitude: ${latitude} and longitude: ${longitude}`);
	}

	const facilityBrand = brandThemeToFacilityBrand(brand);
	const { getBeffeGraphQlClient } = await import('@src/lib/client');
	const facilitiesResponse = await getBeffeGraphQlClient(graphqlUrl)
		.query<{ nearbyFacilities: { items: NearbyFacility[] } }>(
			NEARBY_FACILITIES_QUERY,
			{
				filter: {
					latitude,
					longitude,
					withinRadius,
					brand: facilityBrand as never,
				},
			},
			{ fetchOptions: { headers: getGraphHeaders(appName, appVersion) } }
		)
		.toPromise();

	return (facilitiesResponse.data?.nearbyFacilities as { items: NearbyFacility[] }).items;
});

const brandThemeToFacilityBrand = (brand: TAGBrand) => {
	switch (brand) {
		case TAGBrand.WellNow:
		case TAGBrand.YouWellNow:
		case TAGBrand.LivWellNow:
			return FacilityBrand.WellNow;
		case TAGBrand.ClearChoice:
		case TAGBrand.LobbyClearChoice:
		case TAGBrand.ConsultClearChoice:
			return FacilityBrand.ClearChoice;
		case TAGBrand.TeamTAG:
		case TAGBrand.AspenDental:
			return FacilityBrand.AspenDental;
		case TAGBrand.Motto:
			return 'motto';
		case TAGBrand.Lovet:
			return 'lovet';
		case TAGBrand.Chapter:
			return 'chapter';
		default:
			return FacilityBrand.AspenDental;
	}
};

export const brandThemeToBffBrandType = (brand: TAGBrand): BffBrandType => {
	switch (brand) {
		case TAGBrand.WellNow:
		case TAGBrand.YouWellNow:
		case TAGBrand.LivWellNow:
			return BffBrandType.WellNow;
		case TAGBrand.ClearChoice:
		case TAGBrand.LobbyClearChoice:
		case TAGBrand.ConsultClearChoice:
			return BffBrandType.ClearChoice;
		case TAGBrand.TeamTAG:
		case TAGBrand.AspenDental:
			return BffBrandType.AspenDental;
		case TAGBrand.Lovet:
			return BffBrandType.Lovet;
		case TAGBrand.Chapter:
			return BffBrandType.Chapter;
		default:
			return BffBrandType.AspenDental;
	}
};

export default function useFacilities(
	appName: string,
	appVersion: string,
	brand: TAGBrand,
	bffGraphqlUrl: string,
	useFacilityBeffeDataSource: boolean,
	user?: Maybe<IUser>,
	facilityCode?: string,
	defaultToCdn?: boolean,
	transitionallyUseBeffe?: boolean,
	useSessionStorage?: boolean,
	shouldFetchNearbyFacility?: boolean,
	showSetOfficeButtonInGoogleMapsCards?: boolean
) {
	const {
		storageValue: storedFacilityCode,
		setValue,
		removeItem,
	} = useBrowserStorage({
		key: FACILITY_CODE_STORAGE_KEY,
		storage: useSessionStorage ? globalThis.sessionStorage : globalThis.localStorage,
	});

	const storageFacilityCode = storedFacilityCode?.toString();
	const [officeInfo, setOfficeInfo] = useState<OfficeInfoType>(null);
	const [nearbyFacilities, setNearbyFacilities] = useState<BffFacility[] | undefined | null>(undefined);
	const [isWaitingForNearbyFacilities, setIsWaitingForNearbyFacilities] = useState(shouldFetchNearbyFacility);
	const [locationState, setLocationState] = useState<{
		nearByOfficeLocations: Array<NearbyFacility> | null;
		closestOfficeInfo: NearbyFacility | null;
		distance?: number | null;
		schedulingSystem?: string | null;
		user: IUser;
	}>({
		closestOfficeInfo: null,
		nearByOfficeLocations: null,
		distance: null,
		user: {
			hasIpBasedCoordinates: false,
			coordinates: { latitude: 0, longitude: 0 },
		},
	});

	const getNearbyFacilitiesByGeolocation = useCallback(
		async (officeInformation?: OfficeInfoType) => {
			if (!shouldFetchNearbyFacility) return;

			const handleError = (error: Error | GeolocationPositionError) => {
				console.error('Error occurred: ', error);
				removeItem();
				setNearbyFacilities(null);
			};

			const handleSuccess = (res: NearbyFacility[], nearbyFacilitiesData: BffFacility[] | null) => {
				if (!res || res.length === 0 || res[0].facility === null) {
					removeItem();
					return false;
				}

				setLocationState((prev) => ({ ...prev, nearByOfficeLocations: res, closestOfficeInfo: res[0] }));
				setOfficeInfo(res[0].facility);
				setValue(res[0].facility.code);
				setNearbyFacilities(nearbyFacilitiesData);
				return true;
			};

			const getPosition = (): Promise<GeolocationPosition> => {
				return new Promise((resolve, reject) => {
					globalThis.navigator?.geolocation.getCurrentPosition(resolve, reject, {
						timeout: 10000,
						maximumAge: 60000,
					});
				});
			};

			try {
				let position: { coords: { latitude: number; longitude: number } } | GeolocationPosition = {
					coords: { latitude: 0, longitude: 0 },
				};

				if (officeInformation?.location?.longitude && officeInformation?.location?.latitude) {
					position = {
						coords: {
							latitude: officeInformation.location.latitude as number,
							longitude: officeInformation.location.longitude as number,
						},
					};
				}

				if (defaultToCdn && !officeInformation) {
					position = {
						coords: {
							latitude: user?.coordinates.latitude || 0,
							longitude: user?.coordinates.longitude || 0,
						},
					};
				}

				if (!defaultToCdn && !officeInformation) {
					position = await getPosition();
				}

				let facilities: NearbyFacility[] = [];
				let nearbyFacilitiesData: BffFacility[] | null = null;

				if (useFacilityBeffeDataSource) {
					const res = await getBffNearByFacilitiesByLocation(
						brandThemeToBffBrandType(brand),
						position.coords.latitude || 0,
						position.coords.longitude || 0,
						bffGraphqlUrl,
						appName,
						appVersion
					);
					facilities = mapFacilities(res?.facilityByLocation as any);
					nearbyFacilitiesData =
						res?.facilityByLocation?.filter(
							(facility: BffFacility | null): facility is BffFacility => facility !== null
						) ?? null;
				} else {
					facilities = await getNearbyFacilities({
						appName,
						appVersion,
						graphqlUrl: bffGraphqlUrl,
						brand,
						coords: position.coords || { longitude: 0, latitude: 0 },
					});
				}

				setLocationState((prev) => ({
					...prev,
					user: {
						hasIpBasedCoordinates: defaultToCdn ? user?.hasIpBasedCoordinates : false,
						coordinates: position.coords || { longitude: 0, latitude: 0 },
					},
				}));
				handleSuccess(facilities, nearbyFacilitiesData);
			} catch (error) {
				const geolocationError = error as GeolocationPositionError;
				if (
					(!defaultToCdn &&
						typeof geolocationError === 'object' &&
						'code' in geolocationError &&
						geolocationError.code === geolocationError.PERMISSION_DENIED &&
						user?.hasIpBasedCoordinates) ||
					(user?.coordinates && user?.hasIpBasedCoordinates)
				) {
					try {
						let facilities: NearbyFacility[] = [];
						let nearbyFacilitiesData: BffFacility[] | null = null;

						if (useFacilityBeffeDataSource) {
							const res = await getBffNearByFacilitiesByLocation(
								brandThemeToBffBrandType(brand),
								user?.coordinates.latitude || 0,
								user?.coordinates.longitude || 0,
								bffGraphqlUrl,
								appName,
								appVersion
							);
							facilities = mapFacilities(res?.facilityByLocation as any);
							nearbyFacilitiesData =
								res?.facilityByLocation?.filter(
									(facility: BffFacility | null): facility is BffFacility => facility !== null
								) ?? null;
						} else {
							facilities = await getNearbyFacilities({
								appName,
								appVersion,
								graphqlUrl: bffGraphqlUrl,
								brand,
								coords: user?.coordinates || { longitude: 0, latitude: 0 },
							});
						}

						setLocationState((prev) => ({
							...prev,
							user: {
								hasIpBasedCoordinates: true,
								coordinates: user?.coordinates || { longitude: 0, latitude: 0 },
							},
						}));
						handleSuccess(facilities, nearbyFacilitiesData);
					} catch (err) {
						handleError(err as Error);
					}
				} else {
					handleError(error as Error);
				}
			} finally {
				setIsWaitingForNearbyFacilities(false);
			}
		},
		[
			appName,
			appVersion,
			bffGraphqlUrl,
			useFacilityBeffeDataSource,
			brand,
			setValue,
			removeItem,
			user,
			defaultToCdn,
			shouldFetchNearbyFacility,
		]
	);

	const router = useRouter();
	const facilityCodeFromURL = router.isReady
		? new URL(`${globalThis?.location?.origin || 'http://localhost'}${router.asPath}`).searchParams.get('fc')
		: null;

	useEffect(() => {
		setIsWaitingForNearbyFacilities(true);
		const tempFacilityCode = showSetOfficeButtonInGoogleMapsCards
			? facilityCodeFromURL ?? storageFacilityCode ?? facilityCode ?? ''
			: facilityCodeFromURL ?? facilityCode ?? storageFacilityCode ?? '';

		const getData = async () => {
			if (tempFacilityCode) {
				try {
					let facilityDetails: OfficeInfoType = null;
					if (useFacilityBeffeDataSource || transitionallyUseBeffe) {
						const data = await getBffFacilityById(
							brandThemeToBffBrandType(brand),
							tempFacilityCode,
							bffGraphqlUrl,
							appName,
							appVersion
						);
						const facilities = mapFacilities(data?.facilityById ? [data.facilityById] : null);
						facilityDetails = facilities[0]?.facility ?? null;
					} else {
						facilityDetails = await getFacilityDetails(
							brand,
							tempFacilityCode,
							appName,
							appVersion,
							bffGraphqlUrl
						);
					}

					if (facilityDetails) {
						setValue(tempFacilityCode);
						setOfficeInfo(facilityDetails);
						setIsWaitingForNearbyFacilities(false);
						return;
					}
				} catch (e) {
					console.error('Error fetching facility by code:', e);
				}
			}

			if (shouldFetchNearbyFacility) {
				await getNearbyFacilitiesByGeolocation();
			}
			setIsWaitingForNearbyFacilities(false);
		};

		void getData();
	}, [
		brand,
		appName,
		appVersion,
		getNearbyFacilitiesByGeolocation,
		storageFacilityCode,
		facilityCodeFromURL,
		setValue,
		removeItem,
		facilityCode,
		useFacilityBeffeDataSource,
		bffGraphqlUrl,
		transitionallyUseBeffe,
		shouldFetchNearbyFacility,
		setIsWaitingForNearbyFacilities,
		showSetOfficeButtonInGoogleMapsCards,
	]);

	useEffect(() => {
		if (!officeInfo) return;

		if (locationState.nearByOfficeLocations) {
			setIsWaitingForNearbyFacilities(false);
		} else {
			void getNearbyFacilitiesByGeolocation(officeInfo);
		}
	}, [officeInfo, locationState, getNearbyFacilitiesByGeolocation]);

	return {
		officeInfo,
		facilityCode: storageFacilityCode,
		locationState,
		facilityBrand: brandThemeToFacilityBrand(brand),
		nearbyFacilities,
		isWaitingForNearbyFacilities,
	};
}
